import React from "react";
import { merge, $ } from "glamor";
import { EditorState, ContentState } from "draft-js";
import Editor from "draft-js-plugins-editor";
import createMentionPlugin, { defaultSuggestionsFilter, getStyleMergeMixin } from "components/draft-js-mention";
import { formatMessage } from "lib/globalize-util";
import { theme } from "styles/theme";
import debounce from "lodash/debounce";
import { Set } from "immutable";
import Cancel from "material-ui/svg-icons/navigation/cancel";
import { Search as SearchIcon } from "components/icons/assortments/search";
import classNames from "classnames";
import { DATATEST } from "constants/integration";
import { View } from "constants/product-collection";

const mentionPlugin = createMentionPlugin({
    mentionPrefix: "#",
    mentionTrigger: "#",
});
const { MentionSuggestions } = mentionPlugin;
const plugins = [mentionPlugin];

const Entry = (props) => {
    const {
        mention,
        theme,
        searchValue, // tslint:disable-line no-unused-vars
        isFocused, // tslint:disable-line no-unused-vars
        // tslint:disable-next-line:trailing-comma
        ...parentProps // prettier-ignore
    } = props;

    return (
        <div {...parentProps}>
            <span className={theme.mentionSuggestionsEntryText}>{mention}</span>
        </div>
    );
};

interface IProps {
    onChange: (searchText: string) => void;
    defaultSearchText: { value: string };
    hashtags: Set<string>;
    view: any;
    autoFocus?: boolean;
    onHashtagSearchChange: (searchText: string) => void;
    isServerRollup: boolean;
}

interface IState {
    editorState: any;
    suggestions?: any;
    focus: boolean;
}

export class ProductCollectionHeaderSearch extends React.PureComponent<IProps, IState> {
    container = null;

    state = {
        editorState: undefined,
        suggestions: [],
        focus: false,
    };

    muiStyles = {
        placeholder: {
            color: theme.palette.placeholderText,
        },
    };

    private editor;
    private isMobileiOS;

    private styles = merge(
        {
            // flexGrow: 1,
            flexShrink: 1,
            width: "252px",
            height: "32px",
        },
        $(" .searchContainer", {
            position: "relative",
            paddingRight: "52px",
            height: "32px",
            borderBottom: `1px solid ${theme.palette.grey}`,
        }),
        $(" .DraftEditor-root", {
            height: "32px",
            fontSize: "14px",
            lineHeight: "32px",
            padding: "0",
            width: "100%",
            minWidth: "200px",
        }),
        $(" .DraftEditor-editorContainer, .public-DraftEditor-content", {
            height: "100%",
            width: "100%",
            overflowY: "hidden",
        }),
        $(" .searchIcon", {
            position: "absolute !important",
            top: "50% !important",
            right: "8px !important",
            transform: "translateY(-50%) !important",
            zIndex: 2,
            stroke: theme.palette.grey + " !important",
            opacity: 1,
            height: "18px !important",
            width: "18px !important",
        }),
        $(" .expand", {
            position: "absolute !important",
            top: "75% !important",
            right: "8px !important",
            transform: "translateY(-50%) !important",
            zIndex: 3,
            fill: theme.palette.grey + " !important",
            transition: "all 0.3s ease !important",
            width: "22px !important",
            height: "44px !important",
            cursor: "pointer",
        }),
        $(" .expand.filtersActive", {
            fill: theme.palette.accent1Color + " !important",
        }),
        $(" .cancel", {
            position: "absolute !important",
            top: "50% !important",
            right: "34px",
            transform: "translateY(-50%) !important",
            zIndex: 2,
            fill: theme.palette.grey + " !important",
            transition: "all 0.3s ease !important",
            opacity: 0,
            width: "18px !important",
            height: "18px !important",
        }),
        $(" .cancel.cancelShow", {
            opacity: 1,
            cursor: "pointer !important",
        }),
        $(" .editorContainer", {
            position: "relative",
        }),
        $(" .placeholder", {
            position: "absolute",
            top: "10px",
            left: "10px",
            color: theme.palette.placeholderText,
            fontSize: "14px",
            zIndex: "-1",
        }),
        ...getStyleMergeMixin()
    );

    constructor(props) {
        super(props);

        this.state = {
            editorState: EditorState.createWithContent(
                ContentState.createFromText(props.defaultSearchText.value || "")
            ),
            suggestions: props.hashtags.toJS(),
            focus: false,
        };
        this.isMobileiOS = /iPad|iPhone/.test(window.navigator.userAgent);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.defaultSearchText !== nextProps.defaultSearchText) {
            this.setState({
                editorState: EditorState.createWithContent(
                    ContentState.createFromText(nextProps.defaultSearchText.value || "")
                ),
            });
        }

        if (this.props.hashtags !== nextProps.hashtags) {
            this.setState({
                suggestions: nextProps.hashtags.toJS(),
            });
        }
    }

    componentDidMount() {
        if (this.props.autoFocus) {
            this.handleFocus();
        }
    }

    componentWillUnmount() {
        this.handleDebouncedChange.cancel();
    }

    handleFocus = () => {
        // editor plugin has a bug, need to nextTick it
        // https://github.com/draft-js-plugins/draft-js-plugins/issues/900
        process.nextTick(() => this.editor && this.editor.focus());
    };

    getValue() {
        return this.state.editorState.getCurrentContent().getPlainText().trim();
    }

    handleClick = () => {
        if (!this.getValue()) {
            this.handleFocus();
        }
    };

    bindRef = (el) => (this.container = el);

    render() {
        const { editorState, focus } = this.state;
        const { view } = this.props;

        const cancelShow = !!editorState.getCurrentContent().getPlainText().trim();
        return (
            <div {...this.styles}>
                <div className="searchContainer" ref={this.bindRef}>
                    <SearchIcon className="searchIcon" onClick={this.handleFocus} />
                    <div className="editorContainer" onClick={this.handleClick}>
                        <Editor
                            webDriverTestID={DATATEST.ASSORTMENTS.ASSORTMENT_SEARCH_INPUT}
                            editorState={editorState}
                            onChange={this.handleTextFieldSearchChange}
                            plugins={plugins}
                            ref={this.bindEditor}
                            tabIndex={0}
                            placeholder={this.isMobileiOS ? null : formatMessage("Search Assortment")}
                            stripPastedStyles={true}
                            handlePastedText={this.handlePaste}
                            onBlur={this.handleEditorBlur}
                            onFocus={this.handleEditorFocus}
                            handleKeyCommand={this.handleKeyCommand}
                        />
                        {!this.getValue() && this.isMobileiOS && (
                            <span className="placeholder">{formatMessage("Search Assortment")}</span>
                        )}
                    </div>

                    <Cancel
                        className={classNames("cancel", { cancelShow, tileView: view === View.Tile })}
                        onClick={this.handleClearTextFieldSearch}
                    />
                </div>
                <MentionSuggestions
                    onSearchChange={this.handleHashtagSearchChange}
                    suggestions={this.state.suggestions}
                    entryComponent={Entry}
                    focus={focus}
                />
            </div>
        );
    }

    private handleKeyCommand = (command) => {
        if (command === "split-block") {
            return "handled";
        }
        return "not-handled";
    };

    private handleEditorBlur = () => {
        this.setState({ focus: false });
    };

    private handleEditorFocus = () => {
        this.setState({ focus: true });
    };

    private bindEditor = (ref) => (this.editor = ref);

    // dont allow text with returns to paste
    private handlePaste = (text, html, editorState) => {
        if (text.indexOf("\n") !== -1) {
            return "handled";
        }
        return "not-handled";
    };

    private handleHashtagSearchChange = ({ value }) => {
        if (this.props.isServerRollup) {
            this.props.onHashtagSearchChange(value);
        } else {
            this.setState({
                suggestions: defaultSuggestionsFilter(value, this.props.hashtags.toJS()),
            });
        }
    };

    private handleTextFieldSearchChange = (editorState) => {
        this.setState({
            editorState,
        });
        this.handleDebouncedChange();
    };

    private handleClearTextFieldSearch = () => {
        this.setState({
            editorState: EditorState.createWithContent(ContentState.createFromText("")),
        });
        this.props.onChange("");
        this.handleFocus();
    };

    // tslint:disable-next-line: member-ordering
    private handleDebouncedChange = debounce(() => this.props.onChange(this.getValue()), 800);
}
